import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Form, Input, Select, Popconfirm, notification } from "antd";
import { RuleObject } from "antd/lib/form";
import srv from "../../../../services";
import AppealFiles from "./AppealFiles";
import {
  BaseForm,
  Col,
  SubmitButton,
  Title,
  Alert,
} from "../AppealForm.styled";
import SelectTheme from "../../../_common/SelectTheme";
import ModalNotification from "../ModalNotification/ModalNotification";
import { useHistory } from "react-router-dom";

export default function AppealOfficial() {
  const {
    form,
    isSubmiting,
    error,
    validateAgreement,
    visible,
    handleCancel,
    push,
    ...formProps
  } = useForm();
  const contracts = useContracts();
  const { answerTypes, services, territories } = useDicts();
  const value =
    sessionStorage.getItem("flag") === "true"
      ? JSON.parse(sessionStorage.getItem("appealForm"))
      : {};

  return (
    <>
      <Title>Создание официального обращения</Title>
      <BaseForm
        form={form}
        layout="vertical"
        scrollToFirstError
        initialValues={value}
        {...formProps}
      >
        <Col>
          <Form.Item
            name="contractId"
            label="№ договора"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="Выберите № договора"
            >
              {contracts.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.printNumber}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <SelectTheme />

          <Form.Item
            name="serviceId"
            label="Услуга"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="Выберите услугу"
            >
              {services.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="territoryId"
            label="Представительство"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="Выберите представительство"
            >
              {territories.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col flex={1.3}>
          <Form.Item
            name="message"
            label="Сообщение"
            rules={[{ required: true }]}
          >
            <Input.TextArea autoSize={{ minRows: 18, maxRows: 22 }} />
          </Form.Item>
        </Col>

        <Col flex={1.2}>
          <Form.Item dependencies={["topicId"]} noStyle>
            {({ getFieldValue }) => (
              <AppealFiles topicId={getFieldValue("topicId")} />
            )}
          </Form.Item>

          <Form.Item
            name="answerDeliveryId"
            label="Выберите удобный способ получения ответа"
            rules={[{ required: true }]}
          >
            <Select>
              {answerTypes.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {!!error && <Alert type="error" showIcon message={error} />}
          <Popconfirm
            title="Вы уверены?"
            okButtonProps={{ danger: true }}
            okText="Да"
            cancelText="Отмена"
            onConfirm={() => {
              if (form.getFieldsValue()?.files) {
                const sizeAll = form
                  .getFieldsValue()
                  ?.files.reduce((acc: number, item) => acc + item.size, 0);
                if (sizeAll > 10 * 1024 * 1024) return;

                console.log(form.getFieldsValue());
              }

              form.submit();
            }}
            disabled={isSubmiting}
          >
            <SubmitButton loading={isSubmiting}>Отправить</SubmitButton>
          </Popconfirm>
        </Col>
      </BaseForm>
      <ModalNotification
        handleCancel={handleCancel}
        visible={visible}
        push={() => push("/feedback/appeals")}
      >
        <Alert message="Обращение успешно создано" type="success" />
        <p>
          Для перехода к списку ранее созданных обращений нажмите кнопку
          "Перейти"
        </p>
      </ModalNotification>
    </>
  );
}

function useForm() {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const { push } = useHistory();

  const showModal = () => {
    setVisible(true);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const {
    mutate: save,
    isLoading,
    isSuccess,
    error,
  } = useMutation(srv.saveAppeal, {
    onError: () => {
      notification.error({
        message: "Предупреждение",
        description: `Аналогичное обращение было подано ранее в личном кабинете.
        Повторное обращение создавать в личном кабинете и других средствах коммуникаций не требуется.
        Ответ будет предоставлен в рамках уже созданного обращения.`,
        duration: 0,
        placement: "bottomRight",
      });
    },
  });

  const onFinish = useCallback(
    (values) => {
      (values.serviceId === 1 && values.territoryId === 13) ||
      (values.serviceId === 2 && values.territoryId === 6) ||
      (values.serviceId === 2 && values.territoryId === 7) ||
      (values.serviceId === 2 && values.territoryId === 8) ||
      (values.serviceId === 2 && values.territoryId === 9)
        ? notification.error({
            message: "Предупреждение",
            description:
              "Вы не сможете создать обращение, так как выбранная услуга не обслуживается в данном представительстве",
            duration: 10,
            placement: "bottomRight",
          })
        : save(values);
    },
    [save]
  );

  useEffect(() => {
    if (isSuccess) {
      showModal();
      sessionStorage.removeItem("appealForm");
    }
    return () => {
      sessionStorage.setItem(
        "appealForm",
        JSON.stringify(form.getFieldsValue(true))
      );
      sessionStorage.setItem("flag", JSON.stringify(!isSuccess));
    };
  }, [isSuccess, form]);

  const validateAgreement = useCallback(
    (): RuleObject => ({
      validator(_, value) {
        if (value) return Promise.resolve();
        return Promise.reject("Обязательное поле");
      },
    }),
    []
  );

  return {
    form,
    isSubmiting: isLoading,
    error,
    validateAgreement,
    onFinish,
    visible,
    handleCancel,
    push,
  };
}

function useContracts() {
  const contracts =
    useQueryClient().getQueryData<Models.Contract[]>("contracts");

  return useMemo(() => {
    return contracts.filter((c) => c.state !== "Расторгнут");
  }, [contracts]);
}

function useDicts() {
  const queryClient = useQueryClient();

  const answerTypes = queryClient.getQueryData<Models.Dict[]>("answerTypes");
  const services = queryClient.getQueryData<Models.Dict[]>("services");
  const territories = queryClient.getQueryData<Models.Dict[]>("territories");

  return { answerTypes, services, territories };
}
